/**
 * Shared button styling variants — copied verbatim from
 * `src/modules/oracle/views/features/editor/plate/primitives/button.tsx`
 * so that shared components (Calendar, Dialog, NavItemMenu) can reuse
 * the same visual contract without statically importing from
 * `src/modules/oracle/`.
 *
 * Why: importing from `src/modules/oracle/` pulled the entire Plate.js
 * editor stack into the marketing entry chunk via shared/Calendar →
 * Oracle button → Plate ecosystem. That chunk had a top-level error
 * that crashed React hydration on every marketing route. See
 * `vite.config.ts` manualChunks comments for the broader story.
 *
 * Keep this file in sync with the Oracle definition by hand if either
 * adds a variant — they're meant to render identically.
 */
import { cva, type VariantProps } from 'class-variance-authority'

export const buttonVariants = cva(
  'inline-flex w-fit cursor-pointer items-center justify-center gap-2 rounded-md text-sm focus-ring ring-offset-background transition-bg-ease select-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    defaultVariants: {
      size: 'default',
      truncate: true,
      variant: 'default',
    },
    variants: {
      active: {
        false: '',
        true: 'border-2 border-primary',
      },
      disabled: {
        true: 'pointer-events-none opacity-50',
      },
      focused: {
        true: 'ring-2 ring-ring ring-offset-2',
      },
      isMenu: {
        true: 'h-auto w-full cursor-pointer justify-start',
      },
      size: {
        blockAction: 'size-[26px] gap-1 px-1.5 text-xs',
        default: 'h-[28px] gap-1.5 px-2',
        icon: 'size-[28px] w-[33px] [&_svg]:size-5',
        iconSm: 'size-[28px]',
        lg: 'h-11 px-4 text-lg',
        md: 'h-8 px-3',
        menuAction: 'size-6',
        navAction: 'size-5',
        none: '',
        sm: 'h-7 px-2 text-sm',
        xs: 'h-[26px] px-1.5 py-1 text-xs',
      },
      truncate: {
        true: 'truncate whitespace-nowrap',
      },
      variant: {
        blockAction:
          'rounded-sm hover:bg-primary/[.06] [&_svg]:text-muted-foreground',
        blockActionSecondary:
          'rounded-sm bg-primary/[.06] [&_svg]:text-muted-foreground',
        brand:
          'bg-brand font-medium text-white hover:bg-brand/80 active:bg-brand-active',
        default:
          'bg-primary font-medium text-primary-foreground hover:bg-primary/90 [&_svg]:text-primary-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 [&_svg]:text-destructive-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        ghost2: 'text-muted-foreground hover:bg-accent',
        ghost3: 'text-muted-foreground/80 hover:bg-accent',
        ghostActive: 'bg-accent hover:bg-accent hover:text-accent-foreground',
        menuAction: 'text-muted-foreground/80 hover:bg-primary/[.06]',
        nav: 'rounded-sm text-muted-foreground transition hover:bg-primary/[.04]',
        navAction:
          'rounded-sm text-muted-foreground/80 opacity-0 transition group-hover:opacity-100 hover:bg-primary/[.06]',
        none: '',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        primaryOutline:
          'border border-primary-foreground text-primary-foreground hover:bg-accent/15 [&_svg]:text-primary-foreground',
        radio: 'border-2 border-input hover:border-primary',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
      },
    },
  },
)

export type ButtonVariantProps = VariantProps<typeof buttonVariants>
